/**
 * Отображение карт армянского белота (id 0..31 = suit*8 + rank), синхронно с server/src/game/belot/armenian/types.js.
 */

const RANK_LABEL = ['7', '8', '9', 'J', 'Q', 'K', '10', 'A'];
const SUIT_SYMBOL = ['♣', '♦', '♥', '♠'];
const SUIT_NAME = ['трефы', 'бубны', 'черви', 'пики'];

/** Ключи масти для `PlayingCard` (clubs…spades = 0..3, как id карты / 8). */
const PLAYING_SUIT_KEYS = ['clubs', 'diamonds', 'hearts', 'spades'];

/**
 * id белота 0..31 (suit×8 + rank) → ранг и масть для `PlayingCard`.
 * @param {unknown} cardId
 * @returns {{ rank: string, suit: 'clubs'|'diamonds'|'hearts'|'spades' }}
 */
export function belotCardIdToPlaying(cardId) {
  const c = Number(cardId);
  if (!Number.isInteger(c) || c < 0 || c > 31) return { rank: '7', suit: 'clubs' };
  return { rank: RANK_LABEL[c % 8], suit: PLAYING_SUIT_KEYS[Math.floor(c / 8)] };
}

/**
 * @param {unknown} card
 * @returns {number} 0..3
 */
export function belotCardSuit(card) {
  const c = Number(card);
  if (!Number.isInteger(c) || c < 0 || c > 31) return 0;
  return Math.floor(c / 8);
}

/**
 * @param {unknown} card
 * @returns {number} 0..7
 */
export function belotCardRank(card) {
  const c = Number(card);
  if (!Number.isInteger(c) || c < 0 || c > 31) return 0;
  return c % 8;
}

/**
 * Короткая подпись: «10♥», «A♠».
 * @param {unknown} card
 * @returns {string}
 */
export function formatBelotCard(card) {
  const c = Number(card);
  if (!Number.isInteger(c) || c < 0 || c > 31) return '—';
  const s = belotCardSuit(c);
  const r = belotCardRank(c);
  return `${RANK_LABEL[r]}${SUIT_SYMBOL[s]}`;
}

/**
 * @param {unknown} card
 * @returns {string}
 */
export function formatBelotCardVerbose(card) {
  const c = Number(card);
  if (!Number.isInteger(c) || c < 0 || c > 31) return '—';
  const s = belotCardSuit(c);
  const r = belotCardRank(c);
  return `${RANK_LABEL[r]} ${SUIT_NAME[s]}`;
}

/**
 * @param {number|null|undefined} trumpSuit козырь контракта: 0..3 или null (без козыря)
 * @param {unknown} card
 */
export function belotCardIsTrump(trumpSuit, card) {
  if (trumpSuit === 'all_trump') return true;
  if (trumpSuit == null) return false;
  return belotCardSuit(card) === Number(trumpSuit);
}

/**
 * Класс Tailwind для цвета масти (червы/бубны — красные).
 * @param {unknown} card
 */
export function belotCardColorClass(card) {
  const s = belotCardSuit(card);
  return s === 1 || s === 2 ? 'text-rose-300' : 'text-slate-200';
}

/**
 * Сортировка руки для показа: по масти, внутри — по силе «взятки без козыря» (7…A).
 * @param {number[]} hand
 * @returns {number[]}
 */
export function sortBelotHandForDisplay(hand) {
  if (!Array.isArray(hand)) return [];
  const plainOrder = [0, 1, 2, 3, 4, 5, 6, 7];
  return [...hand]
    .filter((c) => Number.isInteger(c) && c >= 0 && c <= 31)
    .sort((a, b) => {
      const sa = belotCardSuit(a);
      const sb = belotCardSuit(b);
      if (sa !== sb) return sa - sb;
      return plainOrder[belotCardRank(a)] - plainOrder[belotCardRank(b)];
    });
}

/**
 * Подпись козыря для контракта / торговли.
 * @param {unknown} trump число 0..3 или строка 'no_trump'
 */
export function formatBelotTrumpBid(trump) {
  if (trump === 'no_trump') return 'без козыря';
  if (trump === 'all_trump') return 'все козыри';
  const n = Number(trump);
  if (Number.isInteger(n) && n >= 0 && n <= 3) return SUIT_SYMBOL[n];
  return String(trump ?? '—');
}

/**
 * Краткая подпись заявки в торгах (CP 10–25, масть / без козыря, капот).
 * @param {unknown} bid
 */
export function formatAuctionBid(bid) {
  if (!bid || typeof bid !== 'object') return '—';
  const tens = Number(bid.tens);
  if (!Number.isFinite(tens) || tens < 1) return '—';
  const cp = bidCpTensForDisplay(bid);
  if (bid.kapot) {
    return `${cp} CP · капот · ${formatBelotTrumpBid(bid.trump)}`;
  }
  return `${cp} CP · ${formatBelotTrumpBid(bid.trump)}`;
}

/** Число CP для бейджа (внутренний `tens`: 10–24, капот 25). */
export function bidCpTensForDisplay(bid) {
  if (!bid || typeof bid !== 'object') return '—';
  if (bid.kapot) return '25';
  const tens = Number(bid.tens);
  return Number.isFinite(tens) ? String(tens) : '—';
}

/**
 * Полные очки движка → целые CP матча для UI (`Math.round`, как в живой игре / Candywings).
 * @param {unknown} enginePoints
 * @returns {string}
 */
export function belotMatchCpFromEnginePoints(enginePoints) {
  const n = Number(enginePoints);
  if (!Number.isFinite(n)) return '—';
  return String(Math.round(n / 10));
}

/**
 * Цель матча в движке (1510/3010/…) → подпись в CP.
 * @param {unknown} engineTarget
 */
export function belotMatchTargetCpFromEngine(engineTarget) {
  return belotMatchCpFromEnginePoints(engineTarget);
}

/**
 * Контракт из снапшота: CP 10–25 по `tens`/`kapot` или из `contractTargetPoints`.
 * @param {{ contractTargetPoints?: unknown, kapot?: unknown, tens?: unknown }|null|undefined} contract
 */
export function belotContractCpLabelFromEngine(contract) {
  if (!contract || typeof contract !== 'object') return '—';
  if (Boolean(contract.kapot)) return '25';
  const tens = Number(contract.tens);
  if (Number.isFinite(tens) && tens >= 10) return String(Math.trunc(tens));
  const pts = Number(contract.contractTargetPoints);
  if (!Number.isFinite(pts) || pts <= 0) return '—';
  return String(Math.round(pts / 10));
}

/** Одна буква/символ масти для пузыря торгов (Candy-style). */
export function auctionTrumpBubbleGlyph(trump) {
  if (trump === 'no_trump') return 'БК';
  if (trump === 'all_trump') return 'А';
  const n = Number(trump);
  if (Number.isInteger(n) && n >= 0 && n <= 3) return SUIT_SYMBOL[n];
  return '—';
}
